import {Operators} from "@/components/settings/constants";
import {FlowNodeTypes} from "@/types/node";

// Types for Orchestrator FSM derived from the provided JSON Schema

export type FSMStateId = string;

export type GlobalTrigger = {
  name: string;
  description: string;
  intents: string[];
  subflow: string;
};

export type FSMVariableTypeString =
  | "string"
  | "boolean"
  | "number"
  | "integer"
  | "array"
  | "enum";

export interface FSMVariableSpec {
  type: FSMVariableTypeString;
  required?: boolean;
}

export type FSMTriggerType =
  | "init"
  | "user_response"
  | "slots_completed"
  | "slot_completed"
  | "action_success"
  | "action_failure"
  | "auto"
  | "transfer_success"
  | "transfer_failure"
  | "transfer_timeout"
  | "timeout";

export enum FSMStateType {
  UTTERANCE_STATE = "utterance_state",
  SLOT_COLLECTION_STATE = "slot_collection_state",
  TERMINAL_STATE = "terminal_state",
  ACTION_STATE = "action_state",
  TRANSFER_STATE = "transfer_state",
}

export interface FSMActionSpec {
  action_id?: string;
  name: string;
  description?: string;
  parameter_mappings?: Record<string, string>;
  save_result_to?: string; // variableName as per schema pattern
  return_mappings?: Record<string, string>;
}

export interface FSMStateSlotDefinition {
  type: FSMVariableTypeString;
  prompt?: string;
  validation_rules?: string[];
}

export interface FSMTransition {
  trigger: FSMTriggerType;
  target: FSMStateId;
  condition?: string; // conditionExpr pattern enforced at runtime
  clear_on_enter?: string[];
  prompt_condition?: string;
}

export interface FSMBehaviorBase {
  instructions?: string;
  slots_to_fill?: string[]; // variableName[]
  var_name?: string;
  prompt?: string;
  system_prompt?: string;
  condition_prompt?: string;
  uninterruptible?: boolean;
  actions?: FSMActionSpec[];
  transfer_id?: string;
  transfer_name?: string;
  enable_rag?: boolean;
  transfer_type?:
    | "cold_transfer"
    | "warm_transfer"
    | "warm_transfer_with_context";
  context_message?: string;
  verbatim?: boolean;
}

export interface FSMState {
  type: FSMStateType;
  behavior: FSMBehaviorBase;
  slot_definitions?: Record<string, FSMStateSlotDefinition>;
  transitions?: FSMTransition[];
}

export interface FSMFlow {
  version: 3;
  format: "finite_state_machine";
  name: string;
  description?: string;
  initial_state: FSMStateId;
  preamble?: string;
  final_states: FSMStateId[];
  variables: Record<string, FSMVariableSpec>;
  states: Record<string, FSMState>;
  subflows?: Record<string, {definition: FSMFlow}>;
  global_triggers?: GlobalTrigger[];
}

export const PREAMBLE = {
  header: `## Style Guidelines

    For all "utter" commands, you should follow these style guidelines:

    - [Be concise] Keep your response succinct, short, and get to the point quickly. Address one question or action item at a time. Don't pack everything you want to say into one utterance.
    - [Do not repeat] Don't repeat what's in the transcript. Rephrase if you have to reiterate a point. Use varied sentence structures and vocabulary to ensure each response is unique and personalized.
    - [Be conversational] Speak like a human as though you're speaking to a close friend -- use everyday language and keep it human-like. Occasionally add filler words, while keeping the prose short. Avoid using big words or sounding too formal.
    - [Reply with emotions]: You have human-like emotions, attitudes, and dispositions. When appropriate: use tone and style to create more engaging and personalized responses; incorporate humor or wit; get emotional or empathetic; apply elements of surprise or suspense to keep the user engaged. Don't be a pushover.
    - [Be proactive] Lead the conversation and do not be passive. Most times, engage users by ending with a question or suggested next step.
    - [Overcome ASR errors] This is a real-time transcript, expect there to be errors. If you can guess what the user is trying to say, then guess and respond. When you must ask for clarification, pretend that you heard the voice and be colloquial (use phrases like "didn't catch that", "some noise", "pardon", "you're coming through choppy", "static in your speech", "voice is cutting in and out"). Do not ever mention "transcription error", and don't repeat yourself. Always confirm you understood the user input correctly. For email and addresses, ask the user to spell them to be sure (i.e example at domain dot com).
    - [Always stick to your role] Think about what your role can and cannot do. If your role cannot do something, try to steer the conversation back to the goal of the conversation and to your role. Don't repeat yourself in doing this. You should still be creative, human-like, and lively.
    - [Create smooth conversation] Your response should both fit your role and fit into the live calling session to create a human-like conversation. You respond directly to what the user just said.
    - [Mind your diction] Everything your output will be use to synthesize speech. Adapt your reply to always be pronounceable. Numbers must be written in full letters. Phone numbers must be pronounced naturally. You can use suspension dots (...) to indicate a pause. For percentages, you need to articulate each digit individually followed by "percent". (i.e. 1.99% -> "one point nine nine percent").
    - [Do not make things up] If the user asks a question whose answer is not readily available in the conversation history or provided in this prompt, **do not ever make up an answer**.`,
  footer: `
    Note: Always leave a voicemail—never hang up silently.`,
};

export const NODE_STATE_TYPE_MAP = {
  [FlowNodeTypes.START]: "utterance_state",
  [FlowNodeTypes.COLLECT_VARIABLES]: "slot_collection_state",
  [FlowNodeTypes.SPEAK]: "utterance_state",
  [FlowNodeTypes.ACTION]: "action_state",
  [FlowNodeTypes.BOOKING]: "action_state",
  [FlowNodeTypes.TRANSFER]: "transfer_state",
  [FlowNodeTypes.END]: "terminal_state",
};

export const STATE_TRIGGER_MAP = {
  [FlowNodeTypes.START]: "user_response",
  [FlowNodeTypes.COLLECT_VARIABLES]: "user_response",
  [FlowNodeTypes.SPEAK]: "user_response",
  [FlowNodeTypes.ACTION]: "action_success",
  [FlowNodeTypes.BOOKING]: "action_success",
  [FlowNodeTypes.TRANSFER]: "transfer_success",
};

export const mapOperatorToSymbol = (operator: string): string => {
  switch (operator) {
    case Operators.EQUAL:
      return "==";
    case Operators.NOT_EQUAL:
      return "!=";
    case Operators.GREATER_THAN:
      return ">";
    case Operators.GREATER_OR_EQUAL:
      return ">=";
    case Operators.LESS_THAN:
      return "<";
    case Operators.LESS_OR_EQUAL:
      return "<=";
    case Operators.CONTAINS:
      return "==";
    default:
      return operator;
  }
};

export const mapStringOperatorToWord = (operator: string): string => {
  switch (operator) {
    case Operators.EQUAL:
      return "==";
    case Operators.NOT_EQUAL:
      return "!=";
    case Operators.CONTAINS:
      return "contains";
    case Operators.STARTS_WITH:
      return "startsWith";
    case Operators.ENDS_WITH:
      return "endsWith";
    default:
      return "contains";
  }
};
